import { Extension, type Editor } from "@tiptap/core";
import { TextSelection } from "@tiptap/pm/state";

export type TextSize = "normal" | "subtitle" | "title";

export type TextblockSpan = {
  pos: number;
  start: number;
  end: number;
};

export const TEXT_SIZE_ORDER: TextSize[] = ["normal", "subtitle", "title"];

export function textSizeFromNode(name: string, level?: number | null): TextSize {
  if (name === "heading" && level === 2) return "title";
  if (name === "heading" && level === 3) return "subtitle";
  return "normal";
}

export function textSizeToBlock(size: TextSize): { type: "paragraph" | "heading"; attrs: Record<string, number> } {
  if (size === "title") return { type: "heading", attrs: { level: 2 } };
  if (size === "subtitle") return { type: "heading", attrs: { level: 3 } };
  return { type: "paragraph", attrs: {} };
}

/**
 * Only textblocks with real selected characters change size.
 * A caret on the boundary / an expanded range that only touches the
 * previous block's closing position must not convert that previous block.
 */
export function selectTextblocksForSizeChange(
  selection: { from: number; to: number; empty: boolean },
  blocks: TextblockSpan[],
  cursorBlock: TextblockSpan | null,
): TextblockSpan[] {
  if (selection.empty) {
    return cursorBlock ? [cursorBlock] : [];
  }

  const hit = blocks.filter((block) => {
    const overlap = Math.min(selection.to, block.end) - Math.max(selection.from, block.start);
    return overlap > 0;
  });

  return hit.length > 0 ? hit : cursorBlock ? [cursorBlock] : [];
}

export function getCursorTextblock(editor: Editor): TextblockSpan | null {
  const { $from } = editor.state.selection;
  if (!$from.parent.isTextblock) return null;
  return {
    pos: $from.before($from.depth),
    start: $from.start($from.depth),
    end: $from.end($from.depth),
  };
}

export function collectTextblocksInRange(editor: Editor, from: number, to: number): TextblockSpan[] {
  const blocks: TextblockSpan[] = [];
  editor.state.doc.nodesBetween(from, to, (node, pos) => {
    if (node.isTextblock) {
      blocks.push({
        pos,
        start: pos + 1,
        end: pos + node.nodeSize - 1,
      });
    }
  });
  return blocks;
}

export function resolveTextblocksForSizeChange(editor: Editor): TextblockSpan[] {
  const { selection } = editor.state;
  return selectTextblocksForSizeChange(
    { from: selection.from, to: selection.to, empty: selection.empty },
    collectTextblocksInRange(editor, selection.from, selection.to),
    getCursorTextblock(editor),
  );
}

export function getActiveTextSize(editor: Editor): TextSize {
  const { $from } = editor.state.selection;
  return textSizeFromNode($from.parent.type.name, $from.parent.attrs.level);
}

export function applyTextSize(editor: Editor, size: TextSize): boolean {
  if (editor.isDestroyed) return false;

  const targets = resolveTextblocksForSizeChange(editor);
  if (!targets.length) return false;

  const { schema } = editor.state;
  const next = textSizeToBlock(size);
  const nodeType = next.type === "heading" ? schema.nodes.heading : schema.nodes.paragraph;
  if (!nodeType) return false;

  const applied = editor.chain().command(({ tr, dispatch }) => {
    const sorted = [...targets].sort((a, b) => b.pos - a.pos);
    for (const block of sorted) {
      const mappedPos = tr.mapping.map(block.pos);
      const node = tr.doc.nodeAt(mappedPos);
      if (!node?.isTextblock) continue;
      if (node.type === nodeType) {
        if (size === "normal") continue;
        if (node.attrs.level === next.attrs.level) continue;
      }
      const from = mappedPos + 1;
      const to = mappedPos + node.nodeSize - 1;
      tr.setBlockType(from, to, nodeType, next.attrs);
    }
    if (dispatch) dispatch(tr);
    return true;
  }).run();

  // Keep the caret where it is. `.focus()` in the same chain can jump to the
  // end of the doc and, in some environments, insert an empty paragraph.
  if (applied) {
    editor.view?.focus?.();
  }

  return applied;
}

/** Enter after a title/subtitle starts a normal paragraph instead of cloning the heading. */
export function exitHeadingOnEnter(editor: Editor): boolean {
  const { state } = editor;
  const { $from, empty } = state.selection;
  if (!empty || $from.parent.type.name !== "heading") return false;

  const paragraph = state.schema.nodes.paragraph;
  if (!paragraph) return false;

  if ($from.parent.content.size === 0) {
    return editor.commands.setParagraph();
  }

  const atEnd = $from.parentOffset === $from.parent.content.size;
  const atStart = $from.parentOffset === 0;

  if (atStart || atEnd) {
    return editor.commands.command(({ tr, dispatch }) => {
      const insertPos = atStart ? $from.before() : $from.after();
      tr.insert(insertPos, paragraph.create());
      const caret = insertPos + 1;
      if (caret < 0 || caret > tr.doc.content.size) return false;
      tr.setSelection(TextSelection.create(tr.doc, caret));
      if (dispatch) dispatch(tr.scrollIntoView());
      return true;
    });
  }

  return editor.commands.command(({ tr, dispatch }) => {
    const splitPos = $from.pos;
    const end = $from.end();
    const contentAfter = $from.parent.content.cut($from.parentOffset);
    tr.delete(splitPos, end);
    const insertAt = tr.mapping.map($from.after());
    tr.insert(insertAt, paragraph.create(null, contentAfter));
    const caret = insertAt + 1;
    if (caret < 0 || caret > tr.doc.content.size) return false;
    tr.setSelection(TextSelection.create(tr.doc, caret));
    if (dispatch) dispatch(tr.scrollIntoView());
    return true;
  });
}

export const ExitHeadingOnEnter = Extension.create({
  name: "exitHeadingOnEnter",
  priority: 1000,
  addKeyboardShortcuts() {
    return {
      Enter: ({ editor }) => exitHeadingOnEnter(editor),
    };
  },
});

export function normalizePastedHeadingHtml(html: string): string {
  return html
    .replace(/<h1(\s|>)/gi, "<h2$1")
    .replace(/<\/h1>/gi, "</h2>")
    .replace(/<h[456](\s|>)/gi, "<h3$1")
    .replace(/<\/h[456]>/gi, "</h3>");
}
